import * as z from "zod";
import {
  FlowDefinitionSchema,
  parseFlowDefinitionStructure,
  type FlowDefinition,
} from "./schema.js";

const successfullyParsedDefinitions = new WeakMap<object, string>();

function valueFingerprint(value: unknown, ancestors: Set<object>): string | null {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return `string:${JSON.stringify(value)}`;
  if (typeof value === "boolean") return `boolean:${value}`;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return `number:${Object.is(value, -0) ? "-0" : value}`;
  }
  if (typeof value !== "object" || ancestors.has(value)) return null;

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      if (Object.getPrototypeOf(value) !== Array.prototype) return null;
      if (Object.getOwnPropertySymbols(value).length > 0) return null;
      const names = Object.getOwnPropertyNames(value);
      if (names.length !== value.length + 1 || !names.includes("length")) return null;
      const items: string[] = [];
      for (let index = 0; index < value.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
        if (!descriptor || !("value" in descriptor) || !descriptor.enumerable) return null;
        const item = valueFingerprint(descriptor.value, ancestors);
        if (item === null) return null;
        items.push(item);
      }
      return `array:[${items.join(",")}]`;
    }

    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return null;
    if (Object.getOwnPropertySymbols(value).length > 0) return null;
    const entries: string[] = [];
    for (const key of Object.getOwnPropertyNames(value).sort()) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor || !("value" in descriptor) || !descriptor.enumerable) return null;
      const nested = valueFingerprint(descriptor.value, ancestors);
      if (nested === null) return null;
      entries.push(`${JSON.stringify(key)}:${nested}`);
    }
    return `object:{${entries.join(",")}}`;
  } finally {
    ancestors.delete(value);
  }
}

function definitionFingerprint(data: object): string | null {
  return valueFingerprint(data, new Set());
}

const LegacyRouteSchema = z.looseObject({
  condition: z.string().min(1),
  target: z.string().min(1).nullable(),
});

const LegacyNodeDataSchema = z.looseObject({
  skill: z.string().min(1).optional(),
  version: z.string().min(1).optional(),
  label: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  optional: z.boolean().optional(),
  approval: z.looseObject({ mandatory: z.boolean().optional() }).optional(),
  parameters: z.record(z.string(), z.unknown()).optional(),
  phase: z.string().min(1).optional(),
  parallel_group: z.string().min(1).optional(),
  flow: z.string().min(1).optional(),
  pass_context: z.boolean().optional(),
  message: z.string().min(1).optional(),
  routes: z.array(LegacyRouteSchema).optional(),
  assets: z.array(z.string().min(1)).optional(),
});

const LegacyNodeSchema = z.looseObject({
  id: z.string().min(1),
  type: z.string().min(1).optional(),
  skill: z.string().min(1).optional(),
  label: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  data: LegacyNodeDataSchema.optional(),
});

const LegacyEdgeSchema = z.looseObject({
  id: z.string().min(1).optional(),
  source: z.string().min(1),
  target: z.string().min(1),
  type: z.string().min(1).optional(),
});

const LegacyFlowSchema = z.looseObject({
  id: z.string().min(1),
  version: z.string().min(1).optional(),
  name: z.string().min(1),
  description: z.string().min(1).optional(),
  requires: z.array(z.string().min(1)).optional(),
  globals: z.record(z.string(), z.unknown()).optional(),
  entry: z.string().min(1).optional(),
  nodes: z.array(LegacyNodeSchema).optional(),
  edges: z.array(LegacyEdgeSchema).optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/** Stable authored-path failure returned without exposing Zod as part of the public boundary. */
export interface FlowDefinitionIssue {
  /** Dot-separated authored-data path; the empty string identifies the root. */
  path: string;
  /** Human-readable reason the value failed contract validation. */
  message: string;
}

/** Inert legacy edge retained for round-tripping outside the closed authored-v2 vocabulary. */
export type LegacyCompatibilityEdge = {
  id: string;
  source: string;
  target: string;
  type: string;
} & Record<string, unknown>;

/** Normalized content whose nodes are strict v2 while inert legacy edges remain lossless. */
export type ParsedFlowDefinition = Omit<FlowDefinition, "edges"> & {
  edges: Array<FlowDefinition["edges"][number] | LegacyCompatibilityEdge>;
};

/** Normalized authoring or compatibility content, or complete authored-path failures. */
export type FlowDefinitionParseResult =
  | { ok: true; data: ParsedFlowDefinition }
  | { ok: false; errors: FlowDefinitionIssue[] };

function success(data: ParsedFlowDefinition): FlowDefinitionParseResult {
  const fingerprint = definitionFingerprint(data);
  if (fingerprint !== null) successfullyParsedDefinitions.set(data, fingerprint);
  return { ok: true, data };
}

function failure(error: z.ZodError): FlowDefinitionParseResult {
  return {
    ok: false,
    errors: error.issues.map((issue) => ({
      path: issue.path.map(String).join("."),
      message: issue.message,
    })),
  };
}

const LEGACY_DEFAULT_KEYS: Record<string, string> = {
  approval_mode: "approval",
  research_depth: "researchDepth",
  auto_review: "autoReview",
  verbosity: "verbosity",
  cli_mode: "cliMode",
  run_input: "run_input",
};

/**
 * v1 default keys that are dropped outright rather than carried through.
 * Deleting a key from {@link LEGACY_DEFAULT_KEYS} alone does not remove it —
 * `parameters` is an open passthrough bag, so an unmapped key survives under
 * `parameters.<key>`. Dropping has to be explicit.
 */
const DROPPED_LEGACY_DEFAULT_KEYS = new Set(["subagent_mode"]);

function normalizeDefaults(globals: Record<string, unknown> | undefined): unknown {
  if (!globals) return undefined;
  const known: Record<string, unknown> = {};
  const parameters: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(globals)) {
    if (DROPPED_LEGACY_DEFAULT_KEYS.has(key)) continue;
    const normalizedKey = LEGACY_DEFAULT_KEYS[key];
    if (normalizedKey) {
      known[normalizedKey] = key === "approval_mode" && value === "auto_approve" ? "auto" : value;
    } else {
      parameters[key] = value;
    }
  }

  if (Object.keys(parameters).length > 0) known.parameters = parameters;
  return Object.keys(known).length > 0 ? known : undefined;
}

function normalizeMeta(value: Record<string, unknown> | undefined): unknown {
  if (!value) return undefined;
  const meta: Record<string, unknown> = {};
  if (typeof value.icon === "string") meta.icon = value.icon;
  if (typeof value.category === "string") meta.category = value.category;
  if (Array.isArray(value.tags)) meta.tags = value.tags;
  if (typeof value.stage === "string") meta.stage = value.stage;
  if (typeof value.onboarding === "object" && value.onboarding !== null) {
    const onboarding = value.onboarding as Record<string, unknown>;
    if (
      onboarding.input_style === "freeform" ||
      onboarding.input_style === "structured" ||
      onboarding.input_style === "repo"
    ) {
      meta.onboarding = {
        input_style: onboarding.input_style,
        ...(typeof onboarding.placeholder === "string"
          ? { placeholder: onboarding.placeholder }
          : {}),
        ...(Array.isArray(onboarding.fields) ? { fields: onboarding.fields } : {}),
      };
    }
  }
  return Object.keys(meta).length > 0 ? meta : undefined;
}

function normalizeLegacyBinding(value: unknown): unknown {
  if (typeof value === "string") {
    return value.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_match, key: string) => {
      const normalized = LEGACY_DEFAULT_KEYS[key];
      return `\${defaults.${normalized ?? `parameters.${key}`}}`;
    });
  }
  if (Array.isArray(value)) return value.map(normalizeLegacyBinding);
  if (typeof value !== "object" || value === null) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [key, normalizeLegacyBinding(nested)]),
  );
}

/**
 * Converts a recognized v1 definition to strict v2 without mutating the authored input.
 * Non-executable legacy nodes become inert placeholders so dependency and terminal semantics survive.
 */
export function normalizeV1(data: unknown): FlowDefinitionParseResult {
  const legacyResult = LegacyFlowSchema.safeParse(data);
  if (!legacyResult.success) return failure(legacyResult.error);
  const legacy = legacyResult.data;

  const contradiction = (legacy.nodes ?? []).findIndex(
    (node) => node.data?.optional === true && node.data.approval?.mandatory === true,
  );
  if (contradiction !== -1) {
    return {
      ok: false,
      errors: [
        {
          path: `nodes.${contradiction}.data`,
          message: "a node cannot be both optional and a mandatory approval gate",
        },
      ],
    };
  }

  const nodes: Array<{ id: string } & Record<string, unknown>> = [];
  for (const node of legacy.nodes ?? []) {
    const data = node.data ?? {};
    const kind = node.type;

    const label = data.label ?? node.label ?? data.skill ?? node.skill ?? node.id;
    const description = node.description ?? data.description ?? data.message ?? label;
    if (kind === "sub-flow" && data.flow) {
      const delegatedAsset = `flow:${data.flow}`;
      const assets = [...new Set([delegatedAsset, ...(data.assets ?? [])])];
      const parameters = data.parameters ?? {};
      const control = {
        optional: data.optional,
        parallelGroup: data.parallel_group,
      };
      nodes.push({
        id: node.id,
        label,
        description,
        phase: data.phase,
        run: {
          kind: "sub-flow",
          flow: data.flow,
          passContext: data.pass_context,
          assets,
        },
        contract:
          Object.keys(parameters).length > 0
            ? { input: normalizeLegacyBinding(parameters) }
            : undefined,
        control: Object.values(control).some((value) => value !== undefined) ? control : undefined,
      });
      continue;
    }

    if (kind !== "skill") {
      const delegatedAsset = kind === "sub-flow" && data.flow ? `flow:${data.flow}` : undefined;
      const assets = [
        ...new Set([...(delegatedAsset ? [delegatedAsset] : []), ...(data.assets ?? [])]),
      ];
      const parameters = data.parameters ?? {};
      nodes.push({
        id: node.id,
        label,
        description,
        phase: data.phase,
        run: {
          kind: "router",
          routes: [{ when: "default", target: null }],
          assets: assets.length > 0 ? assets : undefined,
        },
        contract: {
          requires: [{ expr: "false" }],
          input:
            Object.keys(parameters).length > 0 ? normalizeLegacyBinding(parameters) : undefined,
        },
        control: { optional: true, parallelGroup: data.parallel_group },
      });
      continue;
    }

    const parameters = { ...(data.parameters ?? {}) };
    const authoredInstruction = parameters.instructions;
    delete parameters.instructions;
    const instruction =
      typeof authoredInstruction === "string" && authoredInstruction.length > 0
        ? authoredInstruction
        : description;
    const skill = data.skill ?? node.skill;
    const skillAsset = skill
      ? `skill:${skill}${data.version !== undefined ? `#${data.version}` : ""}`
      : undefined;
    const assets = [...(skillAsset ? [skillAsset] : []), ...(data.assets ?? [])];
    const control = {
      optional: data.optional,
      parallelGroup: data.parallel_group,
    };

    nodes.push({
      id: node.id,
      label,
      description,
      phase: data.phase,
      run: {
        kind: "agent",
        instruction,
        assets: assets.length > 0 ? assets : undefined,
      },
      contract:
        Object.keys(parameters).length > 0
          ? { input: normalizeLegacyBinding(parameters) }
          : undefined,
      gate: data.approval?.mandatory === true ? { approval: "mandatory" } : undefined,
      control: Object.values(control).some((value) => value !== undefined) ? control : undefined,
    });
  }

  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = (legacy.edges ?? []).map((edge, index) => ({
    ...edge,
    id: edge.id ?? `${edge.source}-${edge.target}-${index}`,
    type: edge.type ?? "optional",
  }));

  const anchor =
    nodes.find((node) => node.id === legacy.entry) ?? (nodes.length > 0 ? nodes[0] : undefined);
  if (anchor && legacy.requires && legacy.requires.length > 0) {
    const run = anchor.run as Record<string, unknown>;
    const existingAssets = Array.isArray(run.assets) ? run.assets : [];
    run.assets = [...new Set([...existingAssets, ...legacy.requires])];
  }

  const candidate = {
    schemaVersion: 2,
    id: legacy.id,
    version: legacy.version ?? "1.0.0",
    name: legacy.name,
    description: legacy.description,
    defaults: normalizeDefaults(legacy.globals),
    entry: legacy.entry !== undefined && nodeIds.has(legacy.entry) ? legacy.entry : undefined,
    nodes,
    edges,
    meta: normalizeMeta({ ...(legacy.metadata ?? {}), ...(legacy.meta ?? {}) }),
  };
  const normalized = parseFlowDefinitionStructure({ ...candidate, edges: [] });
  if (!normalized.success) return failure(normalized.error);
  const parsed: ParsedFlowDefinition = { ...normalized.data, edges };
  return success(parsed);
}

/**
 * Parses strict v2 or normalizes v1, reusing an unchanged successful result across boundaries.
 * Only an absent own schema version permits compatibility normalization.
 */
export function parseFlowDefinition(data: unknown): FlowDefinitionParseResult {
  if (typeof data === "object" && data !== null) {
    const recorded = successfullyParsedDefinitions.get(data);
    if (recorded !== undefined && definitionFingerprint(data) === recorded) {
      return { ok: true, data: data as ParsedFlowDefinition };
    }
  }
  if (typeof data === "object" && data !== null && Object.hasOwn(data, "schemaVersion")) {
    const result = FlowDefinitionSchema.safeParse(data);
    return result.success ? success(result.data) : failure(result.error);
  }
  return normalizeV1(data);
}
